<template>
  <div v-if="categorias">
    <go-back
      :onlyAndroid="true"
      :forceRoute="true"
      :routeName="backDestination"
      :label="backLabel"
      v-if="!mesaId"
    ></go-back>
    <!-- Solo chips y secciones de categorías dentro de su horario -->
    <v-slide-group
      v-model="activeSection"
      class="chips-container"
      center-active
      mandatory
      v-if="categoriasVisibles.length > 0"
    >
      <v-slide-item :value="'mas-vendidos'">
        <v-chip
          class="ma-1"
          @click="toggleMasVendidos"
          :outlined="!filtroMasVendidos"
          color="teal"
        >
          <v-icon left x-small>mdi-star</v-icon>
          Más vendidos
        </v-chip>
      </v-slide-item>
      <v-slide-item v-for="(c, index) in categoriasConProductos" :key="index" :value="c.id">
        <v-chip
          class="ma-1"
          @click="scrollToSection(c.id)"
          :outlined="activeSection !== c.id || filtroMasVendidos"
          color="primary"
        >
          {{ c.descripcion }}
        </v-chip>
      </v-slide-item>
    </v-slide-group>
    <v-container class="pb-0">
      <promo-banner :mostrar-forma-pago="true"></promo-banner>
    </v-container>
    <v-row v-if="filtroMasVendidos">
      <v-col cols="12">
        <h2 class="text-h4">Más vendidos</h2>
        <v-container class="pt-2">
          <v-row dense v-if="loadingMasVendidos">
            <v-col v-for="i in 3" :key="i" cols="12">
              <v-skeleton-loader type="list-item-avatar-two-line"></v-skeleton-loader>
            </v-col>
          </v-row>
          <v-row dense v-else-if="masVendidos.length">
            <v-col v-for="(p, i) in masVendidos" :key="i" cols="12" class="py-0">
              <product-mobile-card :product="p"></product-mobile-card>
            </v-col>
          </v-row>
          <v-alert v-else type="info" dense outlined class="mt-3">
            Todavía no hay productos destacados por ventas.
          </v-alert>
        </v-container>
      </v-col>
    </v-row>
    <template v-else>
    <v-row
      v-for="(c, i) in categoriasVisibles"
      v-show="!categoriasVacias[c.id]"
      :key="`categoria_${i}`"
      :class="{ 'content-with-chips': i === 0 && categoriasVisibles.length > 0 }"
    >
      <v-col :id="c.id" cols="12">
        <h2 class="text-h4">{{ c.descripcion }}</h2>
        <new-product-list-mobile
          :categoriaId="c.id"
          :categoria="c"
          :mesaId="mesaId"
          @products-loaded="onProductsLoaded(c.id, $event)"
        ></new-product-list-mobile>
      </v-col>
    </v-row>
    </template>
    <v-row>
      <carrito-footer></carrito-footer>
    </v-row>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import GoBack from '../GoBack.vue'
import NewProductListMobile from '../mobile/NewProductListMobile.vue'
import ProductMobileCard from '../cards/ProductMobileCard.vue'
import CarritoFooter from '../mobile/CarritoFooter.vue'
import PromoBanner from '../PromoBanner.vue'
import axios from '@/axios/axios-api'
import { restoreCatalogScroll, peekCatalogPosition } from '@/helpers/catalogScroll.js'

export default {
  name: 'NewCategoriasHomeMobile',
  components: { GoBack, NewProductListMobile, ProductMobileCard, CarritoFooter, PromoBanner },
  computed: {
    ...mapGetters('Categorias', ['categorias', 'categoriaDisponible']),
    ...mapGetters('App', ['appPreviousPage', 'appFranquiciaSeleccionada', 'tiendaCerrada']),
    categoriasVisibles () {
      if (!this.categorias) return []
      if (this.tiendaCerrada) return this.categorias
      return this.categorias.filter(c => this.categoriaDisponible(c))
    },
    categoriasConProductos () {
      return this.categoriasVisibles.filter(c => !this.categoriasVacias[c.id])
    },
    backDestination () {
      if (this.appFranquiciaSeleccionada) {
        return { name: 'Sucursales', params: { id: String(this.appFranquiciaSeleccionada) } }
      }
      return { name: 'Tiendas' }
    },
    backLabel () {
      return this.appFranquiciaSeleccionada ? 'Volver a sucursales' : 'Volver a tiendas'
    }
  },
  props: {
    mesaId: {
      type: String,
      default: null
    }
  },
  methods: {
    ...mapActions('Categorias', ['getCategorias']),
    async toggleMasVendidos () {
      this.filtroMasVendidos = !this.filtroMasVendidos
      if (this.filtroMasVendidos) {
        this.activeSection = 'mas-vendidos'
        await this.loadMasVendidos()
      } else if (this.categoriasVisibles.length) {
        this.activeSection = this.categoriasVisibles[0].id
      }
    },
    async loadMasVendidos () {
      this.loadingMasVendidos = true
      try {
        const code = this.$clientCode()
        const { data } = await axios.get(`api/productos/mas-vendidos/${code}`, {
          params: { dias: 30, top: 20 }
        })
        this.masVendidos = (Array.isArray(data) ? data : []).map(p => ({
          ...p,
          esMasVendido: true,
          cant: 1
        }))
      } catch (e) {
        this.masVendidos = []
      } finally {
        this.loadingMasVendidos = false
      }
    },
    scrollToSection (sectionId) {
      this.filtroMasVendidos = false
      this.activeSection = sectionId
      this.$nextTick(() => {
        const section = document.getElementById(sectionId)
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    onProductsLoaded (categoriaId, count) {
      this.$set(this.categoriasVacias, categoriaId, !(count > 0))
    },
    createObserver () {
      const options = {
        root: null,
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.filtroMasVendidos) {
            const match = this.categoriasVisibles.find(
              c => String(c.id) === String(entry.target.id)
            )
            if (match) this.activeSection = match.id
          }
        })
      }, options)

      this.categoriasVisibles.forEach((item) => {
        const section = document.getElementById(item.id)
        if (section) {
          this.observer.observe(section)
        }
      })
    }
  },
  watch: {
    '$route.params.client': {
      handler: async function (newClient, oldClient) {
        if (newClient && newClient !== oldClient) {
          this.$store.commit('Categorias/setCategorias', [])
          this.filtroMasVendidos = false
          this.masVendidos = []
          this.categoriasVacias = {}
          await this.getCategorias()
          this.$nextTick(() => {
            if (this.observer) {
              this.observer.disconnect()
            }
            this.createObserver()
          })
        }
      },
      immediate: false
    }
  },
  async mounted () {
    const shouldRestore = !!peekCatalogPosition()
    if (!shouldRestore) {
      this.$vuetify.goTo(0)
    }
    await this.getCategorias()
    this.$nextTick(() => {
      this.createObserver()
      if (shouldRestore) {
        setTimeout(() => {
          restoreCatalogScroll(this.$route)
        }, 150)
      }
    })
  },
  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  data () {
    return {
      activeSection: null,
      filtroMasVendidos: false,
      masVendidos: [],
      loadingMasVendidos: false,
      categoriasVacias: {},
      observer: null
    }
  }
}
</script>
<style scoped>
.chips-container {
  position: fixed;
  top: 56px;
  /* Fija inmediatamente debajo del header/navbar (56px) */
  left: 0;
  width: 100%;
  background: white;
  padding: 10px 0;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  /* Debajo del drawer temporal de Vuetify (z-index 6+) y modales; antes 1000 pisaba el menú hamburguesa */
  z-index: 4;
}

/* Espacio para el contenido para que no quede oculto bajo la barra fija */
.content-with-chips {
  padding-top: 0; /* El padding lo maneja el contenedor padre (Home.vue) */
}
</style>
